import {ref, reactive, toRaw, watch} from "vue";
import CalendarDateModel from "../Models/CalendarDateModel.js";
import spacetime from "spacetime";
import CalendarConfigModel from "../Models/CalendarConfigModel.js";
import {getNumericTranslation, isNumeric, sortObjectByKeys, uuid} from "../helpers.js";
import SelectedDatesModel from "../Models/SelectedDatesModel.js";

export default class CalendarController
{
    /**
     * @param instance {object}
     * @param props {Object}
     */
    constructor(instance, props)
    {
        this.instance = instance;
        this.uuid = uuid();
        this.hotelID = this.instance.attrs.hotelid ?? null;
        this.hoverDates = ref([]);

        /**
         * @type {CalendarConfigModel}
         */
        this.calendarConfigModel = reactive(props.calendarConfigModel);
        this.elements = props.calendarConfigModel.elements;
        this.tagName = props.calendarConfigModel.rangeMode ? 'div' : 'a';
        this.calendarDateModelsByViewIndex = reactive({});
        /**
         *
         * @type {SelectedDatesModel}
         */
        this.selectedDatesModel = reactive(new SelectedDatesModel(props.calendarConfigModel, this.uuid, this.hotelID));
        this.multiSelectedDatesModel = props.multiSelectedDatesModel;
        this.calendarElement = ref(null);
        this.firstDisplayedDate = ref(
            this.getCalendarStartDate(this.calendarConfigModel.startDate)
        );
        this.createCalendarDateModelsForIndex(this.calendarConfigModel.calendarIndex, 0);
        this.emitSelectedDates();

        watch(() => this.calendarConfigModel.calendarIndex,
            (newValue, oldValue) => {
            this.createCalendarDateModelsForIndex(newValue, oldValue);
        });
    }

    /**
     * @param index {number}
     */
    loadCalendarDates(index)
    {
        if (this.calendarConfigModel.enabledBasicCalendar) {
            CalendarDateModel.setAvailabilityForBasicCalendar(
                this.calendarDateModelsByViewIndex[index]
            );
        } else {
            this.fetchAvailability(index);
        }
    }
    /**
     * @return {boolean}
     */
    canGoForward()
    {
        return spacetime(this.calendarConfigModel.maxDisplayDate).isAfter(this.getLastDisplayDate());
    }
    /**
     * @return {boolean}
     */
    canGoBack()
    {
        return spacetime(this.calendarConfigModel.minDisplayDate).isBefore(this.firstDisplayedDate.value);
    }
    /**
     * @return {string}
     */
    getCalendarMonth()
    {
        const start = spacetime(this.firstDisplayedDate.value);
        const end = spacetime(this.getLastDisplayDate());

        if (window.spacetimeTranslations) {
            start.i18n(window.spacetimeTranslations);
            end.i18n(window.spacetimeTranslations);
        }
        return getNumericTranslation(`<p>${start.format('{month} {year}')}</p>`);
    }
    /**
     * @return {CalendarDateModel}
     */
    getLastDisplayDate()
    {
        let lastDisplayedDate;
        const firstDisplayedDate = this.firstDisplayedDate.value.clone();
        if (this.calendarConfigModel.displayMonthDatesOnly) {
            lastDisplayedDate = firstDisplayedDate.add(1, 'month').subtract(1, 'day');
        }
        else {
            lastDisplayedDate = firstDisplayedDate.add(this.calendarConfigModel.daysToDisplay - 1, 'day');
        }
        return lastDisplayedDate;
    }
    goForward()
    {
        if (this.canGoForward()) {
            this.calendarConfigModel.calendarIndex++;
        }
    }
    goBack()
    {
        if (this.canGoBack()) {
            this.calendarConfigModel.calendarIndex--;
        }
    }
    /**
     * @param index {number}
     * @param calendarDateModels {CalendarDateModel[]}
     */
    setCalendarDateModelsForIndex(index, calendarDateModels)
    {
        this.calendarDateModelsByViewIndex[index] = calendarDateModels;
    }
    /**
     * @return {Object<string, CalendarDateModel>}
     */
    getCalendarDateModels()
    {
        return this.calendarDateModelsByViewIndex[this.calendarConfigModel.calendarIndex];
    }

    /**
     * @param onlyDates {string[]}
     * @return {CalendarDateModel[]}
     */
    getCertainCalendarDateModels(onlyDates)
    {
        if (onlyDates.length == 0) {
            return [];
        }

        const calendarDateModels = this.calendarDateModelsByViewIndex[this.calendarConfigModel.calendarIndex];
        let filteredCalendarDateModels = [];
        for (const date of onlyDates)
        {
            if (calendarDateModels[date]) {
                filteredCalendarDateModels.push(calendarDateModels[date]);
            }
        }

        return filteredCalendarDateModels;
    }


    /**
     * @param spacetimeDate {spacetime}
     * @param forward {Boolean}
     * @return {spacetime}
     */
    getNextCalendarStartDate(spacetimeDate, forward)
    {
        const { displayMonthDatesOnly, daysToDisplay } = this.calendarConfigModel;
        const monthsOrDays = displayMonthDatesOnly ? 'month' : 'day';
        const amount = displayMonthDatesOnly ? 1 : daysToDisplay;
        const method = forward ? 'add' : 'subtract';
        return spacetimeDate.clone()[method](amount, monthsOrDays);
    }
    /**
     * @param index {number}
     * @param oldValue {number}
     */
    createCalendarDateModelsForIndex(index, oldValue)
    {
        let nextStartDate = this.firstDisplayedDate.value;
        const isBooting = Object.keys(this.calendarDateModelsByViewIndex).length === 0;
        if (! isBooting) {
            const isForward = index > oldValue;
            nextStartDate = this.getNextCalendarStartDate(nextStartDate, isForward);
        }
        if (! this.calendarDateModelsByViewIndex[index]) {
            this.calendarDateModelsByViewIndex[index] = this.createCalendarDateModels(nextStartDate);
            this.loadCalendarDates(index);
        }
        this.firstDisplayedDate.value = nextStartDate;
        // The view will now render
    }
    /**
     *
     * @param {string} date
     * @returns {Spacetime}
     */
    getCalendarStartDate(date)
    {
        const { weekStartDay, translations, displayMonthDatesOnly } = this.calendarConfigModel;
        const startOfWeekOrMonth = displayMonthDatesOnly ? 'month' : 'week';
        const spacetimeWeekDayIndex = this.calendarConfigModel.getDayNameIndex(weekStartDay, true);
        let startDate = spacetime(date).weekStart(spacetimeWeekDayIndex).startOf(startOfWeekOrMonth);
        startDate.i18n(translations);
        return startDate.clone();
    }

    /**
     * @param startDate {spacetime}
     * @returns {CalendarDateModel[]}
     */
    createCalendarDateModels(startDate)
    {
        let calendarDateModels = {};
        let { daysToDisplay, displayMonthDatesOnly } = this.calendarConfigModel;
        if (displayMonthDatesOnly) {
            daysToDisplay = startDate.add(1, 'month').subtract(1, 'day').date();
        }
        const calendarStateDetails = this.getCalendarStateDetails();

        for (let i = 0; i < daysToDisplay; ++i)
        {
            const calendarDateModel = new CalendarDateModel(
                startDate.clone().add(i, 'day'),
                calendarStateDetails
            );
            calendarDateModels[calendarDateModel.getDate()] = calendarDateModel;
        }
        if (displayMonthDatesOnly) {
            calendarDateModels = this.getCalendarEmptyDates(calendarDateModels);
        }
        return calendarDateModels;
    }

    /**
     * @param calendarDateModels {CalendarDateModel[]}
     * @return calendarDateModels {CalendarDateModel[]}
     */
    getCalendarEmptyDates(calendarDateModels)
    {
        const firstDaySpacetimeDate = Object.values(calendarDateModels).at(0).getSpacetimeDate();
        const firstDayName = firstDaySpacetimeDate.dayName();
        const emptyDatesToCreate = this.calendarConfigModel.getDayNameIndex(firstDayName);
        const calendarStateDetails = this.getCalendarStateDetails();

        if (emptyDatesToCreate > 0) {
            for (let i = 1; i <= emptyDatesToCreate; ++i) {
                const date = firstDaySpacetimeDate.clone().subtract(i, 'day');
                const emptyCalendarDateModel = new CalendarDateModel(date, calendarStateDetails);
                emptyCalendarDateModel.setIsEmptyDateCell(true);
                calendarDateModels[emptyCalendarDateModel.getDate()] = emptyCalendarDateModel;
            }
        }
        return sortObjectByKeys(calendarDateModels);
    }

    /**
     * The return object is passed to each CalendarDateModel so that it can keep track
     * of details in the calendar such as selected dates
     * @return {Object}
     */
    getCalendarStateDetails()
    {
        return {
            calendarConfigModel: this.calendarConfigModel,
            selectedDatesModel: this.selectedDatesModel,
            multiSelectedDatesModel: this.multiSelectedDatesModel,
            hoverDates: this.hoverDates
        };
    }

    /**
     * @param hoveringCalendarDateModel {CalendarDateModel}
     */
    handleDateHover(hoveringCalendarDateModel)
    {
        this.hoverDates.value = [];

        if (hoveringCalendarDateModel.isEmptyDateCell || this.selectedDatesModel.hasSelectedDates()) {
            return;
        }

        const multiDestHoverDates = this.getSelectableDatesForMultiStayCalendar(hoveringCalendarDateModel);
        let shouldIgnore = false;

        if (multiDestHoverDates.length == 0) {
            /**
             * If range mode is not enabled or there is no selected dates do nothing
             */
            shouldIgnore = (
                ! this.calendarConfigModel.rangeMode ||
                this.selectedDatesModel.isEmpty() ||
                this.selectedDatesModel.hasSelectedDates()
            );
        }

        if (shouldIgnore) {
            return;
        }

        let hoverDates = [];
        if (multiDestHoverDates.length > 0) {
            hoverDates = multiDestHoverDates;
        }
        else {
            hoverDates = CalendarDateModel.getRangeDates(
                this.selectedDatesModel.getArrivalCalendarDateModel(),
                hoveringCalendarDateModel
            );
        }

        this.hoverDates.value = CalendarDateModel.getSelectableRange(
            this.getCertainCalendarDateModels(hoverDates)
        );
    }

    /**
     * @param calendarDateModel {CalendarDateModel}
     * @return {string[]}
     */
    getSelectableDatesForMultiStayCalendar(calendarDateModel)
    {
        const selectedDateModels = this.multiSelectedDatesModel.getSelectedDateModels();
        let hoverDates = [];

        for (const index in selectedDateModels)
        {
            const previousSelectedDateModel = selectedDateModels[index - 1];

            if (! previousSelectedDateModel || ! previousSelectedDateModel.hasSelectedDates()) {
                continue;
            }

            if (calendarDateModel.isCalendarDateBeforeDate(previousSelectedDateModel.getArrivalCalendarDateModel())) {
                hoverDates = CalendarDateModel.getRangeDates(
                    calendarDateModel,
                    previousSelectedDateModel.getArrivalCalendarDateModel()
                );
                break;
            }

            if (calendarDateModel.isCalendarDateAfterDate(previousSelectedDateModel.getDepartureCalendarDateModel())) {
                hoverDates = CalendarDateModel.getRangeDates(
                    previousSelectedDateModel.getDepartureCalendarDateModel(),
                    calendarDateModel
                );
                break;
            }

        }

        return CalendarDateModel.getSelectableRange(
            this.getCertainCalendarDateModels(hoverDates)
        );
    }

    handleMouseExitCalendar()
    {
        this.hoverDates.value = [];
    }
    /**
     * @param calendarDateModel {CalendarDateModel}
     */
    handleDateSelect(calendarDateModel)
    {
        this.hoverDates.value = [];
        const isNotDateClickable = this.selectedDatesModel.isEmpty() && ! calendarDateModel.isClickable();
        /**
         * If the date is not clickable or range mode is disabled
         * do nothing
         */
        if (isNotDateClickable || ! this.calendarConfigModel.rangeMode || calendarDateModel.isAdditionalDate()) {
            return;
        }
        const isLookingToClearSelectedDates = (
            ! this.selectedDatesModel.isEmpty() &&
            this.selectedDatesModel.getArrivalCalendarDateModel().getDate() == calendarDateModel.getDate() ||
            this.selectedDatesModel.hasSelectedDates()
        );
        /**
         * Is selecting the same start date to unselect it
         */
        if (isLookingToClearSelectedDates) {
            this.clearSelectedDates();
            return;
        }
        const isSelectingDateBeforeCurrentStartDate = this.selectedDatesModel.isDateBeforeCurrentSelectedArrivalDate(calendarDateModel);
        /**
         * Is selecting new date or a date before the selected start date
         */
        if (isSelectingDateBeforeCurrentStartDate || this.selectedDatesModel.hasSelectedDates()) {
            this.selectedDatesModel.clearSelectedDates();
        }
        /**
         * Is selecting start date
         */
        if (this.selectedDatesModel.isEmpty() && calendarDateModel.isSelectableForArrival()) {
            this.selectedDatesModel.setCalendarDateModels([calendarDateModel]);

            if (this.calendarConfigModel.enableMultiDestinationCalendar) {
                this.autoSelectMultiStayDates(calendarDateModel);
            }
        }
        /**
         * Is selecting end date
         */
        else if (this.selectedDatesModel.hasOnlySelectedArrivalDate()) {
            const desiredCalendarDateModels = this.getDesiredCalendarDateModels(calendarDateModel);
            const selectableDates = CalendarDateModel.getSelectableRange(desiredCalendarDateModels);

            this.selectedDatesModel.setCalendarDateModels(
                this.getCertainCalendarDateModels(selectableDates)
            );
        }
        this.emitSelectedDates();
    }



    clearSelectedDates()
    {
        this.selectedDatesModel.clearSelectedDates();
        const selectedDateModels = this.multiSelectedDatesModel.getSelectedDateModels();
        const firstSelectedDateModel = selectedDateModels[0];

        let clearAllCalendars = (
            firstSelectedDateModel.isEmpty() &&
            firstSelectedDateModel.getUUID() == this.uuid
        );

        if (clearAllCalendars) {
            for (const selectedDateModel of selectedDateModels)
            {
                selectedDateModel.clearSelectedDates();
            }
        }

        this.emitSelectedDates();
    }

    /**
     * @param calendarDateModel {CalendarDateModel}
     */
    autoSelectMultiStayDates(calendarDateModel)
    {
        const dates = this.getSelectableDatesForMultiStayCalendar(calendarDateModel);

        if (dates.length > 0) {
            this.selectedDatesModel.setCalendarDateModels(
                this.getCertainCalendarDateModels(dates)
            );
        }
    }

    /**
     * @param departureCalendarDateModel {CalendarDateModel}
     * @return {CalendarDateModel[]}
     */
    getDesiredCalendarDateModels(departureCalendarDateModel)
    {
        const desiredDates = CalendarDateModel.getRangeDates(
            this.selectedDatesModel.getArrivalCalendarDateModel(),
            departureCalendarDateModel
        );
        let calendarDateModels = this.getCalendarDateModels();
        let previousPage = this.calendarDateModelsByViewIndex[this.calendarConfigModel.calendarIndex - 1] ?? [];
        let desiredCalendarDateModels = [];

        for (const date of desiredDates)
        {
            let desiredCalendarDateModel = previousPage[date] ?? null;
            if (calendarDateModels[date] && ! calendarDateModels[date].isEmptyDateCell) {
                desiredCalendarDateModel = calendarDateModels[date];
            }
            desiredCalendarDateModels.push(desiredCalendarDateModel);
        }

        return desiredCalendarDateModels;
    }

    emitSelectedDates()
    {
        this.instance.emit('selected-dates', this.selectedDatesModel);
    }

    getForwardCSSClasses()
    {
        return {
            'calendar-btn': true,
            'calendar-forward': true,
            'calendar-btn--disabled': ! this.canGoForward()
        };
    }

    getBackCSSClasses()
    {
        return {
            'calendar-btn': true,
            'calendar-back': true,
            'calendar-btn--disabled': ! this.canGoBack()
        }
    }

    getBackAttrs()
    {
        return {
            'disabled': ! this.canGoBack(),
            'aria-label': 'Previous dates'
        }
    }

    getForwardAttrs()
    {
        return {
            'disabled': ! this.canGoForward(),
            'aria-label': 'Next dates'
        }
    }


    /**
     * @param spacetimeDate {spacetime}
     * @param index {number}
     * @returns {string}
     */
    getCalendarUrl(spacetimeDate, index)
    {
        const url = new URL(this.calendarConfigModel.calendarRequestUrl);

        if (url.href.includes(':hotelID') && isNumeric(this.hotelID)) {
            url.href = url.href.replace(':hotelID', this.hotelID);
        }

        url.searchParams.set('arrivalAsStartDate', true);
        spacetimeDate = spacetimeDate.clone();

        if (this.calendarConfigModel.displayMonthDatesOnly) {
            if (index > 0) {
                spacetimeDate = spacetimeDate.add(1, 'month');
            }
            else if (index < 0) {
                spacetimeDate = spacetimeDate.subtract(1, 'month');
            }

            url.searchParams.set('daysToDisplay', spacetimeDate.daysInMonth());
        }
        else {
            url.searchParams.set('offset', index);
            url.searchParams.set('daysToDisplay', this.calendarConfigModel.daysToDisplay);
        }

        if (this.calendarConfigModel.inventoryOnly) {
            url.searchParams.set('inventoryOnly', this.calendarConfigModel.inventoryOnly);
        }

        const date = spacetimeDate.format(CalendarDateModel.DATE_FORMAT);

        return url.href.replace(/\/\d{4}-\d{2}-\d{2}\//, `/${date}/`);
    }

    /**
     * @param index {number}
     */
    fetchAvailability(index)
    {
        const href = this.getCalendarUrl(this.firstDisplayedDate.value, index);

        const fetchRequest = fetch(href, {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Content-Type': 'application/json',
            }
        });
        const calendarDateModels = Object.values(
            this.calendarDateModelsByViewIndex[index]
        );
        /**
         * Set the response data on CalendarDateModels
         */
        fetchRequest.then(r => r.json()).then(response => {
            const calendarDates = response.calendar;
            if (calendarDates) {
                this.handleSuccessfulFetchCalendarRequest(index, calendarDateModels, calendarDates);
            }
            else {
                this.handleFailedFetchCalendarRequest(index, calendarDateModels);
            }
        });
        /**
         * Double check all dates got a response and mark them as failed if they didn't come back
         * or if the request failed
         */
        fetchRequest.catch(() => {
            this.handleFailedFetchCalendarRequest(index, calendarDateModels)
        });
    }

    handleSuccessfulFetchCalendarRequest(index, calendarDateModels, calendarResponseDates)
    {
        const updatedCalendarModels = calendarDateModels.reduce((updatedCalendarModels, calendarDateModel) => {
            const calendarDateDetails = calendarResponseDates[calendarDateModel.getDate()];
            if (calendarDateDetails) {
                calendarDateModel.setAvailabilityResponse(calendarDateDetails);
            }
            updatedCalendarModels[calendarDateModel.getDate()] = calendarDateModel;
            return updatedCalendarModels;
        }, {});
        this.setCalendarDateModelsForIndex(index, updatedCalendarModels);
    }

    /**
     * @param index {number}
     * @param calendarDateModels {Object<string, CalendarDateModel>}
     */
    handleFailedFetchCalendarRequest(index, calendarDateModels)
    {
        const updatedCalendarModels = calendarDateModels.reduce((updatedCalendarModels, calendarDateModel) => {
            if (calendarDateModel.isLoading()) {
                calendarDateModel.setFailedCalendarDate();
                updatedCalendarModels[calendarDateModel.getDate()] = calendarDateModel;
            }
            return updatedCalendarModels;
        }, {});
        this.setCalendarDateModelsForIndex(index, updatedCalendarModels);
    }

    calculateWindowWidth()
    {
        this.calendarConfigModel._windowWidth = window.innerWidth;
    }
    handleMounted()
    {
        this.calculateWindowWidth();
        window.addEventListener('resize', () => {
            this.calculateWindowWidth();
        });
    }
}
